import {
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton
} from '@material-ui/core';
import { Link } from 'gatsby';
import React, { useEffect, useState } from 'react';
import {
  Award,
  ChevronDown,
  X as CloseIcon,
  CreditCard,
  GitMerge,
  Globe,
  Mail,
  Menu as MenuIcon,
  Sparkles,
  Target,
  Terminal,
  Zap
} from 'lucide-react';
import { CALENDAR_LINK } from '../../constants/demo-link';
import ENV from '../../env.json';
import { useMediaQuery } from '../../hooks/useMediaQuery';
import styled from '../../styled';
import { COLORS } from '../../themes/color';
import { CtaButton } from '../CtaButton';
import { GetStartedDialog } from '../GetStartedDialog';
import { NewBadge } from '../NewBadge';

const NavBarInner = styled('div')`
  display: grid;
  grid-template-columns: 1fr 2fr 1fr;
  align-items: center;
  color: ${COLORS.TEXT};
  transition: 0.7s;
  padding: 0 ${(p) => p.theme.spacing(2)}px;

  a {
    transition: none;
  }

  @media (max-width: 800px) {
    padding: ${(p) => p.theme.spacing(1)}px ${(p) => p.theme.spacing(1)}px;
    grid-template-columns: 90px 1fr;
  }
`;

const Menu = styled('ul')`
  list-style-type: none;
  padding: 0;
  margin: 0;
  margin-block-start: 0;
  margin-block-end: 0;
  color: ${COLORS.TEXT};
  text-align: center;
`;

const MenuItem = styled('li')`
  display: inline-block;
  padding: 24px 12px;

  @media (max-width: 800px) {
    padding: 12px;
  }

  a {
    font-size: 16px;
    position: relative;
  }

  a:before {
    content: '';
    position: absolute;
    display: block;
    width: 100%;
    height: 2px;
    bottom: -6px;
    left: 0;
    background-color: #000;
    transform: scaleX(0);
    transition: transform 0.3s ease;
  }

  a:hover:before {
    transform: scaleX(1);
  }
`;

const RightColumn = styled('div')`
  text-align: right;
  position: relative;
  top: 2px;

  @media (max-width: 800px) {
    font-size: 12px;
    top: 0;
  }

  a {
    position: relative;

    &.MuiButton-root:before {
      background-color: transparent;
      width: 0;
      transform: none;
    }

    &.MuiButton-root {
      transform: scale(1);
      transition: transform 0.1s ease;
    }
    &.MuiButton-root:hover {
      transform: scale(1.1);
    }
  }

  a:before {
    content: '';
    position: absolute;
    display: block;
    width: 100%;
    height: 2px;
    bottom: -6px;
    left: 0;
    background-color: #000;
    transform: scaleX(0);
    transition: transform 0.3s ease;
  }

  a:hover:before {
    transform: scaleX(1);
  }

  .hide-on-mobile {
    @media (max-width: 800px) {
      display: none !important;
    }
  }

  .hide-on-desktop {
    display: none;

    @media (max-width: 800px) {
      display: inline-block;
    }
  }
`;

const SubMenuContainer = styled(MenuItem)`
  position: relative;
  display: inline-block;
  transition: 0.1s opacity linear;

  &:hover .product-submenu {
    display: grid;
    opacity: 1;
    transition: 0.1s opacity linear;
    z-index: 100;
    background-color: #fff;
    pointer-events: auto;
  }

  .product-submenu:hover {
    display: grid;
    opacity: 1;
    z-index: 100;
    pointer-events: auto;
  }

  .product-submenu {
    display: none;
    top: 56px;
    opacity: 0;
    position: absolute;
    left: 50%;
    transform: translateX(-50%);
    border-radius: 14px;
    z-index: 1;
    color: ${COLORS.TEXT};
    width: 660px;
    margin: 0;
    padding: 0;
    list-style: none;
    text-align: left;
    box-shadow: 0 16px 48px rgba(14, 28, 43, 0.14);
    border: 1px solid #e2e8f0;
    overflow: hidden;
    grid-template-columns: 1fr 280px;
    pointer-events: none;

    li {
      list-style-type: none;
      margin: 0;
      padding: 0;
      display: flex;
      min-height: 100%;
    }

    a:before,
    a:hover:before {
      display: none;
      transform: none;
    }

    &.wide {
      width: 780px;
      grid-template-columns: 1fr 280px;
    }
  }
`;

const ColumnLabel = styled('div')`
  padding: 6px 14px 8px;
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: #94a3b8;
`;

const ProductMenuLeft = styled('div')`
  display: flex;
  flex: 1;
  flex-direction: column;
  padding: 14px 10px;
  box-sizing: border-box;
`;

/* Distributes product links evenly so both columns feel balanced. */
const ProductLinksStack = styled('div')`
  display: flex;
  flex: 1;
  flex-direction: column;
  justify-content: space-evenly;
  gap: 2px;
`;

/* 2x2 arrangement for link sets that would otherwise stack too tall. */
const ProductLinksGrid = styled('div')`
  display: grid;
  flex: 1;
  grid-template-columns: 1fr 1fr;
  gap: 4px;
  align-content: space-evenly;
`;

const ProductMenuLink = styled(Link)`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 12px 14px;
  border-radius: 10px;
  text-decoration: none;
  color: inherit;
  transition: background-color 0.15s ease;

  svg {
    flex-shrink: 0;
    color: ${COLORS.PRIMARY};
    margin-top: 2px;
  }

  &:hover {
    background-color: #f0f4ff;

    .link-title {
      color: ${COLORS.PRIMARY};
    }
  }

  &:before {
    display: none;
  }
`;

const FeaturedColumn = styled('div')`
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: 2px;
  padding: 14px 10px;
  background: #f8fafc;
  border-left: 1px solid #e2e8f0;
  box-sizing: border-box;
`;

const FeaturedLink = styled(Link)<{ $fill?: boolean }>`
  display: block;
  padding: 12px 14px;
  border-radius: 10px;
  text-decoration: none;
  color: inherit;
  transition: background-color 0.15s ease;
  ${(p) =>
    p.$fill &&
    `
      flex: 1;
      display: flex;
      flex-direction: column;
      justify-content: center;
    `}

  svg {
    flex-shrink: 0;
    color: ${COLORS.PRIMARY};
  }

  &:hover {
    background-color: #f0f4ff;

    .link-title {
      color: ${COLORS.PRIMARY};
    }
  }

  &:before {
    display: none;
  }
`;

const LinkTitle = styled('span')`
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 700;
`;

const LinkDescription = styled('span')`
  margin-top: 6px;
  color: #666;
  display: block;
  font-size: 14px;
  max-width: 300px;
`;

const MenuLinks = () => {
  const isMobile = useMediaQuery('(max-width: 800px)');

  return (
    <Menu>
      <MenuItem>
        <Link to="/">Home</Link>
      </MenuItem>
      <SubMenuContainer>
        <Link to="/product/">
          Product &nbsp;
          {!isMobile && (
            <ChevronDown
              size={18}
              style={{ position: 'relative', top: '4px' }}
            />
          )}
        </Link>
        {!isMobile && (
          <ul className="product-submenu">
            <li>
              <ProductMenuLeft>
                <ColumnLabel>Products</ColumnLabel>
                <ProductLinksStack>
                  <ProductMenuLink to="/product/unify/">
                    <GitMerge size={18} />
                    <div>
                      <LinkTitle className="link-title">Unify</LinkTitle>
                      <LinkDescription>
                        All your affiliate data from 100+ networks in one place.
                      </LinkDescription>
                    </div>
                  </ProductMenuLink>
                  <ProductMenuLink to="/product/optimize/">
                    <Target size={18} />
                    <div>
                      <LinkTitle className="link-title">Optimize</LinkTitle>
                      <LinkDescription>
                        Heatmaps, offsite tracking, and channel insights for
                        editorial teams.
                      </LinkDescription>
                    </div>
                  </ProductMenuLink>
                  <ProductMenuLink to="/product/amplify/">
                    <Zap size={18} />
                    <div>
                      <LinkTitle className="link-title">
                        Amplify
                        <NewBadge />
                      </LinkTitle>
                      <LinkDescription>
                        Sponsorship and yield optimization campaigns in one
                        workspace.
                      </LinkDescription>
                    </div>
                  </ProductMenuLink>
                </ProductLinksStack>
              </ProductMenuLeft>
            </li>
            <li>
              <FeaturedColumn>
                <ColumnLabel>Featured</ColumnLabel>
                <FeaturedLink to="/ai-platform/" $fill>
                  <LinkTitle className="link-title">
                    <Sparkles size={16} />
                    AI Platform Overview
                  </LinkTitle>
                  <LinkDescription>
                    Agent, campaign execution, and yield optimization on your
                    commerce data.
                  </LinkDescription>
                </FeaturedLink>
                <FeaturedLink to="/mcp/" $fill>
                  <LinkTitle className="link-title">
                    <Terminal size={16} />
                    Affilimate MCP
                  </LinkTitle>
                  <LinkDescription>
                    Your commerce data in Claude and ChatGPT.
                  </LinkDescription>
                </FeaturedLink>
              </FeaturedColumn>
            </li>
          </ul>
        )}
      </SubMenuContainer>
      <SubMenuContainer>
        <Link to="/use-cases/">
          Solutions &nbsp;
          {!isMobile && (
            <ChevronDown
              size={18}
              style={{ position: 'relative', top: '4px' }}
            />
          )}
        </Link>
        {!isMobile && (
          <ul className="product-submenu wide">
            <li>
              <ProductMenuLeft>
                <ColumnLabel>For publishers</ColumnLabel>
                <ProductLinksGrid>
                  <ProductMenuLink to="/use-cases/?tab=news-media">
                    <Globe size={18} />
                    <div>
                      <LinkTitle className="link-title">
                        News and Media Publishers
                      </LinkTitle>
                      <LinkDescription>
                        Uncover exactly how readers interact, shop, and convert
                        across every article and traffic source.
                      </LinkDescription>
                    </div>
                  </ProductMenuLink>
                  <ProductMenuLink to="/use-cases/?tab=newsletter">
                    <Mail size={18} />
                    <div>
                      <LinkTitle className="link-title">
                        Newsletter Publishers
                      </LinkTitle>
                      <LinkDescription>
                        Measure revenue per email, campaign, segment, or
                        subscriber.
                      </LinkDescription>
                    </div>
                  </ProductMenuLink>
                  <ProductMenuLink to="/use-cases/?tab=shopping-loyalty">
                    <Award size={18} />
                    <div>
                      <LinkTitle className="link-title">
                        Shopping & Loyalty Platforms
                      </LinkTitle>
                      <LinkDescription>
                        Power rewards and commerce experiences with real-time
                        affiliate data.
                      </LinkDescription>
                    </div>
                  </ProductMenuLink>
                  <ProductMenuLink to="/use-cases/?tab=finance">
                    <CreditCard size={18} />
                    <div>
                      <LinkTitle className="link-title">
                        Publisher Finance Teams
                      </LinkTitle>
                      <LinkDescription>
                        Track and reconcile affiliate revenue from transaction
                        to payout.
                      </LinkDescription>
                    </div>
                  </ProductMenuLink>
                </ProductLinksGrid>
              </ProductMenuLeft>
            </li>
            <li>
              <FeaturedColumn>
                <FeaturedLink to="/ai-exchange/" $fill>
                  <LinkTitle className="link-title">
                    <Sparkles size={16} />
                    AI Influence Exchange
                  </LinkTitle>
                  <LinkDescription>
                    Turn AI visibility demand into sponsorship revenue. A
                    marketplace for publishers, brands, agencies, and networks.
                    In partnership with ScaleVisible.
                  </LinkDescription>
                </FeaturedLink>
              </FeaturedColumn>
            </li>
          </ul>
        )}
      </SubMenuContainer>
      <MenuItem>
        <Link to="/integrations/">Integrations</Link>
      </MenuItem>
      <MenuItem>
        <Link to="/pricing/">Pricing</Link>
      </MenuItem>
      <MenuItem>
        <Link to={CALENDAR_LINK} target="_blank">
          Demo
        </Link>
      </MenuItem>
    </Menu>
  );
};

const DesktopToggle = styled('div')`
  display: inline-block;
  @media (max-width: 800px) {
    display: none;
  }
`;
const MobileToggle = styled('div')`
  display: none;

  @media (max-width: 800px) {
    display: inline-block;
  }
`;

const NavWrapper = styled('div')<{ scrolled: boolean }>`
  top: 0;
  position: fixed;
  z-index: 100;
  width: 100%;
  transition: background-color 0.3s ease, backdrop-filter 0.3s ease;

  ${(p) =>
    p.scrolled &&
    `
    background-color: rgba(255, 255, 255, 0.5);
    backdrop-filter: blur(10px);
    -webkit-backdrop-filter: blur(10px);
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
  `}
`;

const Navigation = () => {
  const isMobile = useMediaQuery('(max-width: 800px)');
  const [mobileMenu, toggleMobileMenu] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [loginDialogOpen, setLoginDialogOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      const isScrolled = window.scrollY > 20;
      setScrolled(isScrolled);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const seeMenu = (function () {
    if (!isMobile) {
      return false;
    }
    return mobileMenu;
  })();

  return (
    <NavWrapper scrolled={scrolled}>
      <NavBarInner>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <MobileToggle>
            <IconButton onClick={() => toggleMobileMenu(!mobileMenu)}>
              {!mobileMenu ? (
                <MenuIcon color="black" />
              ) : (
                <CloseIcon color="black" />
              )}
            </IconButton>
          </MobileToggle>
          <Link to="/">
            <img
              src="https://storage.googleapis.com/affilimate-assets/logos/logo-black-full.png"
              height="20"
              width="89"
              title="Affilimate Home Page"
              alt="Affilimate"
            />
          </Link>
        </div>
        <DesktopToggle>
          <MenuLinks />
        </DesktopToggle>
        <RightColumn>
          <Button
            variant="text"
            className="hide-on-mobile"
            onClick={(e) => {
              e.preventDefault();
              setLoginDialogOpen(true);
            }}
            style={{
              display: 'inline-block',
              marginLeft: '12px',
              marginRight: '18px',
              cursor: 'pointer'
            }}
          >
            Login
          </Button>
          <CtaButton size="small" onClick={() => setDialogOpen(true)}>
            Get a demo
          </CtaButton>
        </RightColumn>
      </NavBarInner>
      {seeMenu && <MenuLinks />}
      <GetStartedDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
      />
      <Dialog
        open={loginDialogOpen}
        onClose={() => setLoginDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle style={{ textAlign: 'center' }}>
          Log in to your account...
        </DialogTitle>
        <DialogContent>
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '16px',
              paddingTop: '8px'
            }}
          >
            <Button
              variant="contained"
              color="primary"
              fullWidth
              size="large"
              href={ENV.app.baseUrl}
            >
              Publisher Login
            </Button>
            <Button
              variant="outlined"
              color="primary"
              fullWidth
              size="large"
              href="https://demand.affilimate.com"
            >
              Brand & Agency Login
            </Button>
          </div>
          <br />
        </DialogContent>
      </Dialog>
    </NavWrapper>
  );
};

export default Navigation;
